import isFetchBaseQueryError from "~/helpers/isFetchBaseQueryError";
import classifyRtkError, {
	extractStatusCode,
	extractTopLevelGraphqlMessage,
	toGraphqlErrorEntries
} from "~/helpers/rtkErrorClassification";
import {
	hasUncertainEventStoreWrite,
	type MutationPayloadWithErrors,
	type MutationRawResult
} from "~/helpers/runMutation";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";
import { isUnauthorizedError } from "~/services/graphqlErrorHelpers";

export type MutationFireOutcome<TPayload> =
	| { outcome: "success"; payload: TPayload }
	| { outcome: "queued" }
	| { outcome: "failed"; errorMessage: string; sessionRenewed: boolean };

export type UnkeyedMutationFireOutcome<TPayload> =
	| Exclude<MutationFireOutcome<TPayload>, { outcome: "queued" }>
	| { outcome: "unknown" };

export const isQueuedNetworkFailure = (error: unknown): boolean =>
	isFetchBaseQueryError(error) && (error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR");

const failed = (
	errorMessage: string,
	sessionRenewed = false
): Extract<MutationFireOutcome<never>, { outcome: "failed" }> => ({
	errorMessage,
	outcome: "failed",
	sessionRenewed
});

const classifyMutationFireOutcome = <TPayload extends MutationPayloadWithErrors>(
	result: MutationRawResult<unknown>,
	payload: TPayload | null | undefined,
	fallbackMessage: string
): MutationFireOutcome<TPayload> => {
	if (isQueuedNetworkFailure(result.error)) return { outcome: "queued" };

	if (unauthorizedRetryPolicy.isMutationNotReplayedError(result.error)) {
		return failed(extractTopLevelGraphqlMessage(result.error) ?? fallbackMessage, true);
	}

	const payloadMessage = payload?.errors?.[0]?.message;
	if (payloadMessage) return failed(payloadMessage);

	if (result.error) return failed(fallbackMessage);
	if (!payload) return failed(fallbackMessage);

	return { outcome: "success", payload };
};

const UNAUTHENTICATED_STATUS = 401;

const isServerRefusal = (transportError: unknown): boolean => {
	if (!toGraphqlErrorEntries(transportError)) return false;
	if (isUnauthorizedError(transportError) || extractStatusCode(transportError) === UNAUTHENTICATED_STATUS) return false;
	const classification = classifyRtkError(transportError);
	return classification === "client" || classification === "auth";
};

export const classifyUnkeyedMutationOutcome = <TPayload extends MutationPayloadWithErrors>(
	result: MutationRawResult<unknown>,
	payload: TPayload | null | undefined,
	fallbackMessage: string
): UnkeyedMutationFireOutcome<TPayload> => {
	if (result.transportError) {
		return isServerRefusal(result.transportError)
			? failed(extractTopLevelGraphqlMessage(result.transportError) ?? fallbackMessage)
			: { outcome: "unknown" };
	}
	if (!payload) return { outcome: "unknown" };
	if (hasUncertainEventStoreWrite(payload.errors)) return { outcome: "unknown" };
	if (payload.errors?.length) return failed(payload.errors[0]?.message || fallbackMessage);

	return { outcome: "success", payload };
};

export default classifyMutationFireOutcome;
